// Marca de la empresa (perfil de empresa del panel). Se lee una sola vez de
// /api/public/config y se aplica con variables CSS que usan los colores
// `marca` y `acento` de Tailwind (tailwind.config.js) y los de shadcn.
import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { setDefaultLang } from "@/i18n/booking";

const DEFAULT_PRIMARY = "#1E3F20";
const DEFAULT_ACCENT = "#C05A3E";
const HEX = /^#[0-9a-fA-F]{6}$/;

let configPromise = null;

export function loadConfig() {
  if (!configPromise) {
    configPromise = api.get("/public/config").then(({ data }) => {
      // Antes que cualquier otro consumidor: quien elija idioma ya ve el de la empresa.
      if (data?.brand?.default_lang) setDefaultLang(data.brand.default_lang);
      return data;
    }).catch(() => {
      configPromise = null;
      return null;
    });
  }
  return configPromise;
}

export function loadBrand() {
  return loadConfig().then((c) => (c && c.brand) || null);
}

export function assetUrl(path) {
  return path ? `${API.replace(/\/api$/, "")}${path}` : "";
}

export function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHsl([r, g, b]) {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === R) h = (G - B) / d + (G < B ? 6 : 0);
  else if (max === G) h = (B - R) / d + 2;
  else h = (R - G) / d + 4;
  return [Math.round(h * 60), Math.round(s * 100), Math.round(l * 100)];
}

function hslToRgb([h, s, l]) {
  const S = s / 100;
  const L = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const a = S * Math.min(L, 1 - L);
  const f = (n) => L - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

// Tono para el «hover»: el mismo color, un poco más claro.
export function hoverOf(hex) {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  return hslToRgb([h, s, Math.min(l + 8, 92)]);
}

// Contraste del texto blanco sobre un color (WCAG). Por debajo de 4,5 cuesta leer.
export function whiteContrast(hex) {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return 1.05 / (lum + 0.05);
}

export function applyBrand(brand) {
  if (!brand) return;
  const root = document.documentElement.style;
  const rgb = (name, v) => root.setProperty(name, v.join(" "));
  const hsl = (name, hex) => {
    const [h, s, l] = rgbToHsl(hexToRgb(hex));
    root.setProperty(name, `${h} ${s}% ${l}%`);
  };
  const primary = brand.color_primary;
  if (HEX.test(primary || "") && primary.toUpperCase() !== DEFAULT_PRIMARY) {
    rgb("--marca-rgb", hexToRgb(primary));
    rgb("--marca-hover-rgb", hoverOf(primary));
    ["--primary", "--ring", "--accent-foreground"].forEach((n) => hsl(n, primary));
  }
  const accent = brand.color_accent;
  if (HEX.test(accent || "") && accent.toUpperCase() !== DEFAULT_ACCENT) {
    rgb("--acento-rgb", hexToRgb(accent));
    rgb("--acento-hover-rgb", hoverOf(accent));
    hsl("--secondary", accent);
  }
  if (brand.brand_name) document.title = brand.brand_name;
  if (brand.default_lang) setDefaultLang(brand.default_lang);
}

export function useBrand() {
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    let alive = true;
    loadBrand().then((b) => alive && setBrand(b));
    return () => {
      alive = false;
    };
  }, []);
  return brand;
}
